import envService from '@/services/env.service';
import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { PropsWithChildren, createContext, useContext } from 'react';

type AmplitudeValue = any;

const AmplitudeContext = createContext<AmplitudeValue>(amplitude);

export function useAmplitude() {
  const context = useContext(AmplitudeContext);

  if (!context) throw new Error('useAmplitude must be used within a AmplitudeProvider');

  return context;
}

export function AmplitudeProvider({ children }: PropsWithChildren) {
  // Create and Install Session Replay Plugin
  const amplitudeSampleRate = envService.getEnv().VITE_AMPLITUDE_SAMPLE_RATE;
  // If the sample rate is not set, use remote configuration or default is 0
  const sampleRate = amplitudeSampleRate && amplitudeSampleRate !== '' ? Number(amplitudeSampleRate) : undefined;
  const sessionReplayTracking = sessionReplayPlugin({
    sampleRate,
    // Use proxy URL if configured, empty string falls back to Amplitude default
    trackServerUrl: envService.getEnv().VITE_AMPLITUDE_SR_PROXY_URL || '',
  });
  amplitude.add(sessionReplayTracking);

  amplitude.init(envService.getEnv().VITE_AMPLITUDE_API_KEY, {
    autocapture: {
      attribution: {
        resetSessionOnNewCampaign: true,
      },
      pageViews: false,
      sessions: true,
      formInteractions: false,
      fileDownloads: false,
      elementInteractions: false,
    },
    // Use proxy URL if configured, empty string falls back to Amplitude default
    serverUrl: envService.getEnv().VITE_AMPLITUDE_PROXY_URL || '',
  });

  return <AmplitudeContext.Provider value={amplitude}>{children}</AmplitudeContext.Provider>;
}
